import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, Upload, Paperclip, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { celebrate } from "@/lib/haptics";

const COUNTRIES = ["India", "South Korea", "Japan", "UAE", "United States", "United Kingdom", "Singapore", "Australia", "Canada", "Germany", "Malaysia", "Thailand"];
const SENDER_KYC = ["Aadhar", "Driving Licence", "Passport"];
const RECEIVER_KYC = ["Local Resident ID", "Passport"];
const BOOKING_TYPES = [
  { value: "P2P", label: "Personal → Personal", sender: "personal", receiver: "personal" },
  { value: "C2P", label: "Company → Personal", sender: "company", receiver: "personal" },
  { value: "C2C", label: "Company → Company", sender: "company", receiver: "company" },
];
const emptyParty = (country) => ({ entity_type: "personal", full_name: "", company_name: "", contact_person: "", gstin: "", iec_code: "", ad_code: "", phone: "", address: "", city: "", state: "", pincode: "", country, kyc_type: "", kyc_number: "" });
const emptyItem = () => ({ name: "", quantity: "1", value: "" });
const emptyBox = () => ({ length: "", width: "", height: "", weight: "", items: [emptyItem()] });

export default function BookingForm({ guest = false, courier = false, editOrder = null, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [sender, setSender] = useState(() => (editOrder ? { ...emptyParty("India"), ...(editOrder.sender || {}) } : emptyParty("India")));
  const [receiver, setReceiver] = useState(() => (editOrder ? { ...emptyParty(""), ...(editOrder.receiver || {}) } : emptyParty("")));
  const [boxes, setBoxes] = useState(() => {
    if (!editOrder) return [emptyBox()];
    const bx = (editOrder.boxes || []).map((b) => ({
      length: String(b.length ?? ""), width: String(b.width ?? ""), height: String(b.height ?? ""), weight: String(b.weight ?? ""),
      items: (b.items && b.items.length ? b.items : [emptyItem()]).map((it) => ({ name: it.name || "", quantity: String(it.quantity ?? "1"), value: String(it.value ?? "") })),
    }));
    return bx.length ? bx : [emptyBox()];
  });
  const [bookingType, setBookingType] = useState(() => editOrder?.booking_type || "P2P");
  const [commercialInvoice, setCommercialInvoice] = useState(() => editOrder?.commercial_invoice_number || "");
  const [documents, setDocuments] = useState(() => editOrder?.documents || []);
  const [uploading, setUploading] = useState(false);
  const [portals, setPortals] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [guestContact, setGuestContact] = useState({ name: "", email: "", phone: "" });

  const portalsEndpoint = guest ? "/public/portals" : "/portals";
  const quoteEndpoint = guest ? "/public/quote" : "/quote";

  useEffect(() => { api.get(portalsEndpoint).then((r) => setPortals(r.data)).catch(() => {}); }, [portalsEndpoint]);

  useEffect(() => {
    const w = boxes.reduce((sum, b) => sum + (parseFloat(b.weight) || 0), 0);
    if (!receiver.country || w <= 0) { setEstimate(null); return; }
    let active = true;
    api.get(quoteEndpoint, { params: { destination: receiver.country, service: "DDP", weight: w } })
      .then((r) => { if (active) setEstimate(r.data?.price ?? null); })
      .catch(() => { if (active) setEstimate(null); });
    return () => { active = false; };
  }, [receiver.country, boxes, quoteEndpoint]);

  const activeCountries = new Set(["India", ...portals.filter((p) => p.status === "active").map((p) => p.destination_country)]);
  const countryOptions = COUNTRIES.filter((c) => activeCountries.has(c) || c === sender.country || c === receiver.country);

  const resetForm = () => {
    setSender(emptyParty("India"));
    setReceiver(emptyParty(""));
    setBoxes([emptyBox()]);
    setBookingType("P2P");
    setCommercialInvoice("");
    setDocuments([]);
    setGuestContact({ name: "", email: "", phone: "" });
  };

  const changeBookingType = (v) => {
    const bt = BOOKING_TYPES.find((b) => b.value === v);
    setBookingType(v);
    setSender((s) => ({ ...s, entity_type: bt.sender }));
    setReceiver((s) => ({ ...s, entity_type: bt.receiver }));
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocuments((d) => [...d, data]);
      }
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const removeDocument = (id) => setDocuments((d) => d.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        sender: { ...sender },
        receiver: { ...receiver },
        boxes: boxes.map((b) => ({
          length: parseFloat(b.length) || 0, width: parseFloat(b.width) || 0,
          height: parseFloat(b.height) || 0, weight: parseFloat(b.weight) || 0,
          items: b.items.map((it) => ({ name: it.name, quantity: parseInt(it.quantity) || 1, value: parseFloat(it.value) || 0 })),
        })),
        booking_type: bookingType,
        commercial_invoice_number: commercialInvoice,
        documents,
        quoted_price: estimate,
      };
      let result;
      if (editOrder) {
        result = (await api.put(`/courier/orders/${editOrder.id}`, payload)).data;
        toast.success("Shipment updated!");
      } else if (courier) {
        result = (await api.post("/courier/booking", payload)).data;
        toast.success("Booking created!");
      } else if (guest) {
        payload.guest_name = guestContact.name;
        payload.guest_email = guestContact.email;
        payload.guest_phone = guestContact.phone;
        result = (await api.post("/public/booking", payload)).data;
        toast.success("Booking created!");
      } else {
        result = (await api.post("/orders", payload)).data;
        toast.success("Booking created!");
      }
      if (!editOrder && !guest) resetForm();
      if (!editOrder) celebrate("success");
      onCreated?.(result);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const setBox = (i, k) => (e) => setBoxes((bs) => bs.map((b, idx) => (idx === i ? { ...b, [k]: e.target.value } : b)));
  const addBox = () => setBoxes((bs) => [...bs, emptyBox()]);
  const removeBox = (i) => setBoxes((bs) => (bs.length > 1 ? bs.filter((_, idx) => idx !== i) : bs));
  const setItem = (bi, ii, k) => (e) => setBoxes((bs) => bs.map((b, idx) => (idx === bi ? { ...b, items: b.items.map((it, j) => (j === ii ? { ...it, [k]: e.target.value } : it)) } : b)));
  const addItem = (bi) => setBoxes((bs) => bs.map((b, idx) => (idx === bi ? { ...b, items: [...b.items, emptyItem()] } : b)));
  const removeItem = (bi, ii) => setBoxes((bs) => bs.map((b, idx) => (idx === bi ? { ...b, items: b.items.length > 1 ? b.items.filter((_, j) => j !== ii) : b.items } : b)));

  const partyFields = (who, state, setState, kycOptions) => {
    const isCompany = state.entity_type === "company";
    return (
    <div className="space-y-3">
      {isCompany && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Company Name</Label><Input value={state.company_name} onChange={(e) => setState((s) => ({ ...s, company_name: e.target.value }))} required data-testid={`${who}-company-input`} /></div>
          <div className="space-y-1.5"><Label>GST / Tax ID</Label><Input value={state.gstin} onChange={(e) => setState((s) => ({ ...s, gstin: e.target.value }))} required data-testid={`${who}-gstin-input`} /></div>
        </div>
      )}
      {isCompany && who === "sender" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Import Export Code (IEC)</Label><Input value={state.iec_code} onChange={(e) => setState((s) => ({ ...s, iec_code: e.target.value }))} required data-testid="sender-iec-input" /></div>
          <div className="space-y-1.5"><Label>AD Code</Label><Input value={state.ad_code} onChange={(e) => setState((s) => ({ ...s, ad_code: e.target.value }))} required data-testid="sender-adcode-input" /></div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>{isCompany ? "Contact Person" : "Full Name"}</Label><Input value={isCompany ? state.contact_person : state.full_name} onChange={(e) => setState((s) => ({ ...s, [isCompany ? "contact_person" : "full_name"]: e.target.value }))} required data-testid={`${who}-name-input`} /></div>
        <div className="space-y-1.5"><Label>Phone Number</Label><Input value={state.phone} onChange={(e) => setState((s) => ({ ...s, phone: e.target.value }))} required data-testid={`${who}-phone-input`} /></div>
      </div>
      <div className="space-y-1.5"><Label>Full Address</Label><Input value={state.address} onChange={(e) => setState((s) => ({ ...s, address: e.target.value }))} required data-testid={`${who}-address-input`} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-1.5"><Label>City</Label><Input value={state.city} onChange={(e) => setState((s) => ({ ...s, city: e.target.value }))} required data-testid={`${who}-city-input`} /></div>
        <div className="space-y-1.5"><Label>State</Label><Input value={state.state} onChange={(e) => setState((s) => ({ ...s, state: e.target.value }))} data-testid={`${who}-state-input`} /></div>
        <div className="space-y-1.5"><Label>Pincode</Label><Input value={state.pincode} onChange={(e) => setState((s) => ({ ...s, pincode: e.target.value }))} required data-testid={`${who}-pincode-input`} /></div>
        <div className="space-y-1.5"><Label>Country</Label>
          {who === "sender" ? (
            <Input value="India" disabled readOnly data-testid="sender-country-fixed" />
          ) : (
          <Select value={state.country} onValueChange={(v) => setState((s) => ({ ...s, country: v }))}>
            <SelectTrigger data-testid={`${who}-country-select`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{countryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          )}
        </div>
      </div>
      {!isCompany && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>KYC Type</Label>
            <Select value={state.kyc_type} onValueChange={(v) => setState((s) => ({ ...s, kyc_type: v }))}>
              <SelectTrigger data-testid={`${who}-kyc-type-select`}><SelectValue placeholder="Select ID type" /></SelectTrigger>
              <SelectContent>{kycOptions.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>KYC Number <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={state.kyc_number} onChange={(e) => setState((s) => ({ ...s, kyc_number: e.target.value }))} data-testid={`${who}-kyc-number-input`} /></div>
        </div>
      )}
    </div>
    );
  };

  const resolvedPortal = portals.find((p) => p.destination_country === receiver.country);

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="create-order-form">
      {guest && (
        <section data-testid="guest-contact-section">
          <h3 className="font-display font-bold text-lg">Your Contact Details</h3>
          <p className="text-xs text-muted-foreground mb-3">We'll send your tracking link to this email.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Full Name</Label><Input value={guestContact.name} onChange={(e) => setGuestContact((s) => ({ ...s, name: e.target.value }))} required data-testid="guest-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={guestContact.email} onChange={(e) => setGuestContact((s) => ({ ...s, email: e.target.value }))} required data-testid="guest-email-input" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={guestContact.phone} onChange={(e) => setGuestContact((s) => ({ ...s, phone: e.target.value }))} required data-testid="guest-phone-input" /></div>
          </div>
        </section>
      )}

      <section className={guest ? "border-t border-border pt-5" : ""}>
        <Label className="text-sm font-semibold">Booking Type</Label>
        <Select value={bookingType} onValueChange={changeBookingType}>
          <SelectTrigger className="mt-1.5" data-testid="booking-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{BOOKING_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
        </Select>
      </section>

      <section className="border-t border-border pt-5">
        <h3 className="font-display font-bold text-lg">Sender Details</h3>
        <p className="text-xs text-muted-foreground mb-3">{sender.entity_type === "company" ? "Company pickup party (GST required)" : "Pickup party (KYC required)"}</p>
        {partyFields("sender", sender, setSender, SENDER_KYC)}
      </section>

      <section className="border-t border-border pt-5">
        <h3 className="font-display font-bold text-lg">Receiver Details</h3>
        <p className="text-xs text-muted-foreground mb-3">{receiver.entity_type === "company" ? "Company delivery party (GST required)" : "Delivery party (KYC required)"}</p>
        {partyFields("receiver", receiver, setReceiver, RECEIVER_KYC)}
      </section>

      <section className="border-t border-border pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-bold text-lg">Packaging Information</h3>
            <p className="text-xs text-muted-foreground">{boxes.length} box{boxes.length === 1 ? "" : "es"}</p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addBox} data-testid="add-box-button">
            <Plus className="h-4 w-4 mr-1" /> Add Box
          </Button>
        </div>
        <div className="space-y-3">
          {boxes.map((b, i) => (
            <div key={i} className="rounded-xl border border-border p-3 space-y-3" data-testid={`box-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Box {i + 1}</span>
                {boxes.length > 1 && (
                  <Button type="button" size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 h-7 px-2"
                    onClick={() => removeBox(i)} data-testid={`remove-box-${i}`}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="space-y-1"><Label className="text-xs">L (cm)</Label><Input type="number" step="0.1" value={b.length} onChange={setBox(i, "length")} required data-testid={`box-${i}-length`} /></div>
                <div className="space-y-1"><Label className="text-xs">W (cm)</Label><Input type="number" step="0.1" value={b.width} onChange={setBox(i, "width")} required data-testid={`box-${i}-width`} /></div>
                <div className="space-y-1"><Label className="text-xs">H (cm)</Label><Input type="number" step="0.1" value={b.height} onChange={setBox(i, "height")} required data-testid={`box-${i}-height`} /></div>
                <div className="space-y-1"><Label className="text-xs">Wt (kg)</Label><Input type="number" step="0.1" value={b.weight} onChange={setBox(i, "weight")} required data-testid={`box-${i}-weight`} /></div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items in this box</Label>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => addItem(i)} data-testid={`box-${i}-add-item`}><Plus className="h-4 w-4 mr-1" /> Item</Button>
                </div>
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                  <span className="col-span-6">Item Name</span><span className="col-span-2">Qty</span><span className="col-span-3">Value ₹</span><span className="col-span-1" />
                </div>
                {b.items.map((it, j) => (
                  <div key={j} className="grid grid-cols-12 gap-2 items-center" data-testid={`box-${i}-item-${j}`}>
                    <Input className="col-span-6" value={it.name} onChange={setItem(i, j, "name")} placeholder="e.g. Cotton Shirt" required data-testid={`box-${i}-item-${j}-name`} />
                    <Input className="col-span-2" type="number" min="1" value={it.quantity} onChange={setItem(i, j, "quantity")} required data-testid={`box-${i}-item-${j}-qty`} />
                    <Input className="col-span-3" type="number" step="0.01" value={it.value} onChange={setItem(i, j, "value")} placeholder="0" required data-testid={`box-${i}-item-${j}-value`} />
                    <button type="button" className="col-span-1 text-rose-600 disabled:opacity-30 flex justify-center" disabled={b.items.length === 1} onClick={() => removeItem(i, j)} data-testid={`box-${i}-remove-item-${j}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {sender.entity_type === "company" && (
        <section className="border-t border-border pt-5 space-y-3" data-testid="export-docs-section">
          <div>
            <h3 className="font-display font-bold text-lg">Commercial Invoice{!guest && " & Documents"}</h3>
            <p className="text-xs text-muted-foreground">Required for company shipments{!guest && " · attachments optional"}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Commercial Invoice Number</Label>
            <Input value={commercialInvoice} onChange={(e) => setCommercialInvoice(e.target.value)} placeholder="e.g. INV/2026/0045" required data-testid="commercial-invoice-input" />
          </div>
          {!guest && (
            <div className="space-y-2">
              <Label>Upload Documents <span className="text-muted-foreground font-normal">(optional · PDF/JPG/PNG)</span></Label>
              <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 cursor-pointer hover:bg-secondary/40 transition-colors w-fit" data-testid="upload-doc-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span className="text-sm">{uploading ? "Uploading…" : "Choose file(s)"}</span>
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={uploading}
                  onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="upload-doc-input" />
              </label>
              {documents.length > 0 && (
                <div className="space-y-1.5" data-testid="uploaded-docs-list">
                  {documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-1.5 text-sm" data-testid={`uploaded-doc-${d.id}`}>
                      <span className="flex items-center gap-2 truncate"><Paperclip className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{d.original_filename}</span></span>
                      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600" onClick={() => removeDocument(d.id)} data-testid={`remove-doc-${d.id}`}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {!courier && (
      <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 flex items-center justify-between mt-6" data-testid="quote-estimate">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-primary">Estimated Price</div>
          <div className="text-[11px] text-muted-foreground">
            {resolvedPortal ? `Handled by ${resolvedPortal.name} · rates & branding by admin` : "Auto-calculated from admin rate cards"}
          </div>
        </div>
        <div className="font-display text-2xl font-extrabold text-primary">
          {estimate != null ? `₹${estimate.toLocaleString("en-IN")}` : "—"}
        </div>
      </div>
      )}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={creating} data-testid="submit-order-button">
          {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {guest ? "Book Shipment" : "Create Booking"}
        </Button>
      </div>
    </form>
  );
}
